import {useEffect, useState} from 'react';
import * as yup from 'yup';

import {
  POOL_PAYMENT_SENT_AT_MESSAGES,
  validatePoolPaymentSentAt,
} from '@afriex/core/utils';

import {merchantTransactionService} from '@/services/merchants/transactionService';
import {AfriexCountryCode} from '@/types';
import {getErrorMessage} from '@/utils/errorMessage';
import {logRejection} from '@/utils/logRejection';
import {showErrorMessage} from '@/utils/showErrorMessage';
import {showSuccessMessage} from '@/utils/showSuccessMessage';

import {
  getPoolDepositDuplicateWarning,
  type PoolDepositDuplicateWarning,
} from './poolDepositDuplicate';
import type {PoolDepositFormData} from './types';

const senderDetailsSchema = yup.object({
  name: yup.string().trim().min(1).required(),
  accountNumber: yup.string().trim(),
  bankName: yup.string().trim(),
  // Optional: an operator often has the sender's bank but not their country.
  countryCode: yup.string().oneOf(Object.values(AfriexCountryCode)),
});

const REFERENCE_LOOKUP_DEBOUNCE_MS = 400;

export type ReferenceLookupStatus =
  'idle' | 'loading' | 'success' | 'not-found' | 'error';

export interface ReferenceLookupState {
  status: ReferenceLookupStatus;
  businessName?: string;
  customerName?: string;
}

const schema = yup.object({
  reference: yup.string().trim().min(1).required(),
  amount: yup.number().positive().required(),
  country: yup.string().oneOf(Object.values(AfriexCountryCode)).required(),
  // Required, matching the business portal and the public API; the rule lives
  // in core so all three enforce the same one.
  paymentSentAt: yup
    .date()
    .required()
    // A static message: this form builds none (it only gates Confirm on
    // `isValidSync`), and `.required()` already covers the missing case.
    .test(
      'pool-payment-sent-at',
      POOL_PAYMENT_SENT_AT_MESSAGES.future,
      (value) => !validatePoolPaymentSentAt(value),
    ),
  senderDetails: senderDetailsSchema.default(undefined),
});

/**
 * The form as validated and sent: sender fields trimmed, blank optional ones
 * left out (the server refuses '', #7355), and an empty sender block dropped.
 * A blank name is kept, so the schema still rejects a sender without one.
 */
const toSubmittableForm = ({
  senderDetails,
  ...rest
}: Partial<PoolDepositFormData>): Partial<PoolDepositFormData> => {
  const name = senderDetails?.name.trim() ?? '';
  const accountNumber = senderDetails?.accountNumber?.trim();
  const bankName = senderDetails?.bankName?.trim();
  const countryCode = senderDetails?.countryCode;

  if (!name && !accountNumber && !bankName && !countryCode) {
    return rest;
  }

  return {
    ...rest,
    senderDetails: {
      name,
      ...(accountNumber ? {accountNumber} : {}),
      ...(bankName ? {bankName} : {}),
      ...(countryCode ? {countryCode} : {}),
    },
  };
};

const useUploadPoolTransaction = (onClose: VoidFunction) => {
  const [formData, setFormData] = useState<Partial<PoolDepositFormData>>({
    reference: '',
    amount: undefined,
    country: undefined,
    // Prefilled with now so the form opens valid; an older wire is corrected.
    paymentSentAt: new Date(),
  });
  const [isLoading, setIsLoading] = useState(false);
  const [referenceLookup, setReferenceLookup] = useState<ReferenceLookupState>({
    status: 'idle',
  });
  const [duplicateWarning, setDuplicateWarning] = useState<
    PoolDepositDuplicateWarning | undefined
  >();

  const isValid = schema.isValidSync(toSubmittableForm(formData));

  const reference = formData.reference ?? '';

  useEffect(() => {
    const trimmed = reference.trim();

    if (!trimmed) {
      setReferenceLookup({status: 'idle'});
      return;
    }

    let isCurrent = true;

    const lookup = async () => {
      setReferenceLookup({status: 'loading'});
      try {
        const result =
          await merchantTransactionService.resolvePoolReference(trimmed);
        if (!isCurrent) {
          return;
        }
        if (result.found) {
          setReferenceLookup({
            status: 'success',
            businessName: result.businessName,
            customerName: result.customerName,
          });
        } else {
          setReferenceLookup({status: 'not-found'});
        }
      } catch {
        if (!isCurrent) {
          return;
        }
        setReferenceLookup({status: 'error'});
      }
    };

    // `setTimeout` wants a `=> void` callback, so the debounced lookup is its
    // own function with a rejection handler rather than an async callback whose
    // rejection would escape. `lookup` already catches into the 'error' state,
    // so this only fires if that catch itself throws.
    const timer = setTimeout(() => {
      lookup().catch(logRejection('Failed to resolve pool deposit reference'));
    }, REFERENCE_LOOKUP_DEBOUNCE_MS);

    return () => {
      isCurrent = false;
      clearTimeout(timer);
    };
  }, [reference]);

  const handleSubmit = async (acknowledgeDuplicate: boolean = false) => {
    setIsLoading(true);
    try {
      await merchantTransactionService.confirmPoolDeposit({
        ...(toSubmittableForm(formData) as PoolDepositFormData),
        ...(acknowledgeDuplicate ? {acknowledgeDuplicate: true} : {}),
      });
      setDuplicateWarning(undefined);
      showSuccessMessage('Pool deposit confirmed successfully');
      setTimeout(() => {
        window.location.reload();
      }, 1000);
      onClose();
    } catch (e) {
      // A matching pool deposit already exists (already credited, or awaiting
      // review after the entity submitted proof of it). Surface it as a
      // confirmation prompt rather than a dead-end error toast, mirroring the
      // review panel's approve flow. It is the only way past the guard, and
      // waiting does not clear it. Only on the first attempt; a retry that
      // still comes back duplicate falls through to a plain error toast.
      const duplicate = acknowledgeDuplicate
        ? undefined
        : getPoolDepositDuplicateWarning(e);
      if (duplicate) {
        setDuplicateWarning(duplicate);
        return;
      }
      showErrorMessage(
        `Error confirming deposit: ${getErrorMessage(e, String(e))}`,
      );
    } finally {
      setIsLoading(false);
    }
  };

  const confirmSubmitDuplicate = () => handleSubmit(true);
  const dismissDuplicateWarning = () => setDuplicateWarning(undefined);

  return {
    formData,
    setFormData,
    isLoading,
    isValid,
    handleSubmit,
    referenceLookup,
    duplicateWarning,
    confirmSubmitDuplicate,
    dismissDuplicateWarning,
  };
};

export default useUploadPoolTransaction;
